import {
    type AgentReceipt,
    agentReceiptSchema,
} from "@wags/agent-contracts/agent-receipt.contract.ts";
import { z } from "zod";

export { type AgentReceipt, agentReceiptSchema };

import {
    agentActorSchema,
    agentMessagePartSchema,
    timestampSchema,
} from "./common.contract.ts";

const inputEnvelopeSchema = z.object({
    inputId: z.string().min(1),
    createdAt: timestampSchema,
});

export const STAFF_ANSWERED_USER_EXTERNAL_KEY = "staffAnsweredUser";

export const UNDELIVERED_REPLY_EXTERNAL_KEY = "undeliveredReply";

export const messageSubmittedInputSchema = inputEnvelopeSchema
    .extend({
        type: z.literal("message.submitted"),
        actor: agentActorSchema,
        content: z.array(agentMessagePartSchema).min(1),
        visibility: z.literal("internal").optional(),
        response: z.enum(["automatic", "none"]),
        supersession: z.enum(["interrupt-active", "queue"]).optional(),
        external: z.record(z.string(), z.json()).optional(),
    })
    .strict();

export const turnCancelRequestedInputSchema = inputEnvelopeSchema
    .extend({
        type: z.literal("turn.cancel.requested"),
        turnId: z.string().min(1).optional(),
        mode: z.enum(["interrupt", "discard"]),
        reason: z.enum([
            "new-message",
            "user-requested",
            "timeout",
            "shutdown",
        ]),
    })
    .strict();

export const sessionAutomationChangedInputSchema = inputEnvelopeSchema
    .extend({
        type: z.literal("session.automation.changed"),
        actor: agentActorSchema,
        state: z.enum(["active", "escalated", "archived"]),
        preserveActiveTurnId: z.string().min(1).optional(),
        reason: z.string().min(1).optional(),
        summary: z.string().min(1).optional(),
    })
    .strict();

export const agentInputSchema = z.discriminatedUnion("type", [
    messageSubmittedInputSchema,
    turnCancelRequestedInputSchema,
    sessionAutomationChangedInputSchema,
]);

export type AgentInput = z.infer<typeof agentInputSchema>;
export type MessageSubmittedInput = z.infer<typeof messageSubmittedInputSchema>;
